import { useState, useRef, useEffect, useCallback } from 'react'
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
  type PercentCrop,
  type PixelCrop,
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { haptic } from '../../lib/native'

export interface CropRect {
  x: number // 0..1 normalized
  y: number // 0..1 normalized
  width: number // 0..1 normalized
  height: number // 0..1 normalized
}

interface StudioCropOverlayProps {
  imageUrl?: string
  cropRatio?: string // '', 'free', '1:1', '3:4', '9:16', '16:9'
  cropRect?: CropRect
  onChange: (rect: CropRect) => void
  containerRef?: React.RefObject<HTMLDivElement | null>
  zoomTransform?: { scale: number; position: { x: number; y: number } }
  rotation?: number
  flipHorizontal?: boolean
  flipVertical?: boolean
}

const getAspectFromRatio = (ratio?: string): number | undefined => {
  if (!ratio || ratio === 'free') return undefined
  const parts = ratio.split(':').map(Number)
  const w = parts[0]
  const h = parts[1]
  if (parts.length === 2 && typeof w === 'number' && typeof h === 'number' && w > 0 && h > 0) {
    return w / h
  }
  return undefined
}

export function StudioCropOverlay({
  imageUrl,
  cropRatio = 'free',
  cropRect,
  onChange,
  containerRef,
  rotation = 0,
  flipHorizontal = false,
  flipVertical = false,
}: StudioCropOverlayProps) {
  const imgRef = useRef<HTMLImageElement | null>(null)
  const prevEmittedRef = useRef<CropRect | null>(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Source URL taking rotation and flip into account
  const [renderedSrc, setRenderedSrc] = useState<string>(() => {
    return imageUrl || containerRef?.current?.querySelector('img')?.src || ''
  })

  useEffect(() => {
    const sourceUrl = imageUrl || containerRef?.current?.querySelector('img')?.src
    if (!sourceUrl) return

    if (!rotation && !flipHorizontal && !flipVertical) {
      setRenderedSrc(sourceUrl)
      return
    }

    let cancelled = false
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = sourceUrl
    img.onload = () => {
      if (cancelled) return
      const origW = img.naturalWidth || img.width || 800
      const origH = img.naturalHeight || img.height || 600
      const swaps = Math.abs((rotation || 0) % 180) === 90
      const w = swaps ? origH : origW
      const h = swaps ? origW : origH

      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      ctx.translate(w / 2, h / 2)
      ctx.rotate(((rotation || 0) * Math.PI) / 180)
      ctx.scale(flipHorizontal ? -1 : 1, flipVertical ? -1 : 1)
      ctx.drawImage(img, -origW / 2, -origH / 2, origW, origH)

      try {
        canvas.toBlob((blob) => {
          if (cancelled || !blob) return
          const url = URL.createObjectURL(blob)
          setRenderedSrc((prev) => {
            if (prev && prev.startsWith('blob:')) {
              URL.revokeObjectURL(prev)
            }
            return url
          })
        }, 'image/jpeg', 0.95)
      } catch {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.95)
        if (!cancelled) setRenderedSrc(dataUrl)
      }
    }

    return () => {
      cancelled = true
    }
  }, [imageUrl, containerRef, rotation, flipHorizontal, flipVertical])

  useEffect(() => {
    return () => {
      if (renderedSrc && renderedSrc.startsWith('blob:')) {
        URL.revokeObjectURL(renderedSrc)
      }
    }
  }, [renderedSrc])

  // Compute normalized crop emission
  const emitCrop = useCallback((percentCrop: Crop) => {
    if (!percentCrop.width || !percentCrop.height) return

    const normX = Math.max(0, Math.min(1, percentCrop.x / 100))
    const normY = Math.max(0, Math.min(1, percentCrop.y / 100))
    const normW = Math.max(0.01, Math.min(1 - normX, percentCrop.width / 100))
    const normH = Math.max(0.01, Math.min(1 - normY, percentCrop.height / 100))

    const prev = prevEmittedRef.current
    if (
      prev &&
      Math.abs(prev.x - normX) < 0.0005 &&
      Math.abs(prev.y - normY) < 0.0005 &&
      Math.abs(prev.width - normW) < 0.0005 &&
      Math.abs(prev.height - normH) < 0.0005
    ) {
      return
    }

    const nextRect: CropRect = {
      x: normX,
      y: normY,
      width: normW,
      height: normH,
    }
    prevEmittedRef.current = nextRect
    onChangeRef.current(nextRect)
  }, [])

  // Crop state
  const [crop, setCrop] = useState<Crop>(() => {
    if (cropRect) {
      return {
        unit: '%',
        x: cropRect.x * 100,
        y: cropRect.y * 100,
        width: cropRect.width * 100,
        height: cropRect.height * 100,
      }
    }
    return {
      unit: '%',
      x: 5,
      y: 5,
      width: 90,
      height: 90,
    }
  })

  const aspect = getAspectFromRatio(cropRatio)

  // Re-calculate crop when ratio changes
  const applyAspectOrInitCrop = useCallback((aspectVal?: number) => {
    const image = imgRef.current
    if (image && image.width && image.height) {
      const { width, height } = image
      if (aspectVal) {
        const newCrop = centerCrop(
          makeAspectCrop(
            {
              unit: '%',
              width: 85,
            },
            aspectVal,
            width,
            height
          ),
          width,
          height
        )
        setCrop(newCrop)
        emitCrop(newCrop)
        return
      }
    }

    if (aspectVal) {
      // Fallback aspect box if image not yet measured
      const w = 85
      const h = Math.min(85, w / aspectVal)
      const newCrop: PercentCrop = {
        unit: '%',
        x: (100 - w) / 2,
        y: (100 - h) / 2,
        width: w,
        height: h,
      }
      setCrop(newCrop)
      emitCrop(newCrop)
    } else if (!cropRect) {
      const freeCrop: PercentCrop = {
        unit: '%',
        x: 5,
        y: 5,
        width: 90,
        height: 90,
      }
      setCrop(freeCrop)
      emitCrop(freeCrop)
    }
  }, [cropRect, emitCrop])

  // Handle ratio changes
  const prevRatioRef = useRef(cropRatio)
  useEffect(() => {
    if (prevRatioRef.current !== cropRatio) {
      prevRatioRef.current = cropRatio
      const newAspect = getAspectFromRatio(cropRatio)
      applyAspectOrInitCrop(newAspect)
      void haptic('selection')
    }
  }, [cropRatio, applyAspectOrInitCrop])

  const handleCropChange = (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    setCrop(percentCrop)
    emitCrop(percentCrop)
  }

  const handleCropComplete = (_pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    emitCrop(percentCrop)
    void haptic('soft')
  }

  return (
    <div
      className="studio-crop-overlay-root"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 15,
        overflow: 'hidden',
        padding: 0,
        boxSizing: 'border-box',
        touchAction: 'none',
      }}
    >
      <ReactCrop
        crop={crop}
        onChange={handleCropChange}
        onComplete={handleCropComplete}
        aspect={aspect}
        ruleOfThirds
        keepSelection
        minWidth={24}
        minHeight={24}
        className="studio-react-crop"
        ariaLabels={{
          cropArea: 'Crop selection area',
          nwDragHandle: 'Crop top-left',
          nDragHandle: 'Crop top',
          neDragHandle: 'Crop top-right',
          eDragHandle: 'Crop right',
          seDragHandle: 'Crop bottom-right',
          sDragHandle: 'Crop bottom',
          swDragHandle: 'Crop bottom-left',
          wDragHandle: 'Crop left',
        }}
      >
        <img
          ref={imgRef}
          src={renderedSrc || undefined}
          alt="Crop preview"
          onLoad={() => {
            if (aspect) {
              applyAspectOrInitCrop(aspect)
            }
          }}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: '18px',
            display: 'block',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            pointerEvents: 'none',
          }}
        />
      </ReactCrop>
    </div>
  )
}
